/**
 * PaymentProofTable Component
 * Table displaying payment proofs
 */

import React from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import type { PaymentProof } from "../../../store/api/paymentProofsApi";

interface PaymentProofTableProps {
	proofs: PaymentProof[];
	isLoading: boolean;
	onProofClick: (proof: PaymentProof) => void;
	onApprove: (proof: PaymentProof) => void;
	onReject: (proof: PaymentProof) => void;
}

const PaymentProofTable: React.FC<PaymentProofTableProps> = ({
	proofs,
	isLoading,
	onProofClick,
	onApprove,
	onReject,
}) => {
	const formatCurrency = (amount: number) => {
		return new Intl.NumberFormat("en-NG", {
			style: "currency",
			currency: "NGN",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount);
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	const getStatusBadge = (status: string) => {
		const statusConfig: Record<string, { color: string; label: string }> = {
			pending: { color: "bg-warning/10 text-warning", label: "Pending" },
			approved: { color: "bg-success/10 text-success", label: "Approved" },
			rejected: { color: "bg-error/10 text-error", label: "Rejected" },
		};

		const config = statusConfig[status] || statusConfig.pending;
		return (
			<span
				className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
			>
				{config.label}
			</span>
		);
	};

	if (isLoading) {
		return (
			<div className="bg-card border border-border rounded-lg p-12">
				<div className="flex items-center justify-center">
					<Icon
						name="Loader2"
						size={32}
						className="animate-spin text-primary"
					/>
				</div>
			</div>
		);
	}

	if (proofs.length === 0) {
		return (
			<div className="bg-card border border-border rounded-lg p-12 text-center">
				<Icon
					name="FileCheck"
					size={48}
					className="mx-auto mb-4 text-muted-foreground opacity-50"
				/>
				<h3 className="text-lg font-semibold text-foreground mb-2">
					No Payment Proofs
				</h3>
				<p className="text-muted-foreground">
					No payment proofs match your current filters.
				</p>
			</div>
		);
	}

	return (
		<div className="bg-card border border-border rounded-lg overflow-hidden">
			<div className="overflow-x-auto -mx-6 sm:mx-0">
				<div className="inline-block min-w-full align-middle sm:px-0">
					<table className="min-w-full divide-y divide-border">
						<thead className="bg-muted/50 border-b border-border">
							<tr>
								<th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
									Property
								</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
									Amount
								</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
									Payment Method
								</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
									Transaction Ref
								</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
									Status
								</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
									Date
								</th>
								<th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
									Actions
								</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-border">
							{proofs.map((proof) => (
								<tr
									key={proof.id}
									className="hover:bg-muted/30 smooth-transition cursor-pointer"
									onClick={() => onProofClick(proof)}
								>
									<td className="px-4 py-4">
										<p className="text-sm font-medium text-foreground truncate max-w-xs">
											{proof.property_name || "N/A"}
										</p>
									</td>
									<td className="px-4 py-4">
										<p className="text-sm font-medium text-foreground">
											{formatCurrency(proof.amount)}
										</p>
									</td>
									<td className="px-4 py-4">
										<p className="text-sm text-foreground">
											{proof.payment_method}
										</p>
									</td>
									<td className="px-4 py-4">
										<p className="text-sm text-muted-foreground font-mono">
											{proof.transaction_reference || "N/A"}
										</p>
									</td>
									<td className="px-4 py-4">{getStatusBadge(proof.status)}</td>
									<td className="px-4 py-4">
										<p className="text-sm text-muted-foreground">
											{formatDate(proof.payment_date)}
										</p>
									</td>
									<td className="px-4 py-4">
										<div
											className="flex items-center justify-end space-x-2"
											onClick={(e) => e.stopPropagation()}
										>
											{proof.status === "pending" && (
												<>
													<Button
														variant="outline"
														size="sm"
														onClick={() => onApprove(proof)}
													>
														<Icon name="Check" size={16} className="mr-1" />
														Approve
													</Button>
													<Button
														variant="outline"
														size="sm"
														onClick={() => onReject(proof)}
													>
														<Icon name="X" size={16} className="mr-1" />
														Reject
													</Button>
												</>
											)}
											<Button
												variant="ghost"
												size="icon"
												onClick={() => onProofClick(proof)}
												title="View Details"
											>
												<Icon name="Eye" size={16} />
											</Button>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
};

export default PaymentProofTable;
